/**
 * Sprint 4I1.C — ProvinciaPicker (autocomplete / typeahead)
 *
 * Combobox shadcn (Popover + Command/cmdk) per selezionare una provincia
 * da master_data_province. Memorizza la SIGLA (codice, es. "TO", "MI", "RM").
 *
 *   Props: value, onChange, disabled, testId, placeholder, compact
 *
 * Ricerca su: sigla (codice), nome, regione.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";

// Cache process-wide
let _provCache = null;
let _provPromise = null;

async function loadProvince() {
  if (_provCache) return _provCache;
  if (_provPromise) return _provPromise;
  _provPromise = api.get("/master-data/province?attivo=true").then((r) => {
    _provCache = r.data || [];
    _provPromise = null;
    return _provCache;
  }).catch(() => {
    _provPromise = null;
    return [];
  });
  return _provPromise;
}

export function invalidateProvinceCache() {
  _provCache = null;
  _provPromise = null;
}

export function useProvince() {
  const [list, setList] = useState([]);
  useEffect(() => {
    let m = true;
    loadProvince().then((d) => { if (m) setList(d); });
    return () => { m = false; };
  }, []);
  return list;
}

export default function ProvinciaPicker({
  value, onChange, disabled, testId, placeholder = "Provincia…", compact = false,
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadProvince().then((d) => {
      if (mounted) {
        setList(d);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  const norm = (value || "").toString().toUpperCase();
  const selected = useMemo(
    () => list.find((p) => (p.codice || "").toUpperCase() === norm) || null,
    [list, norm],
  );

  const heightCls = compact ? "h-8 text-xs px-2" : "h-10 text-sm px-3";

  const triggerLabel = selected
    ? <span className="font-mono">{selected.codice}<span className="text-slate-500 ml-1.5">— {selected.nome}</span></span>
    : value
      ? <span className="font-mono">{norm}</span>
      : <span className="text-slate-400">{loading ? "…" : placeholder}</span>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || loading}
            data-testid={testId}
            className={cn(
              "w-full justify-between font-normal bg-white",
              heightCls,
              value && !disabled ? "pr-12" : "",
            )}
          >
            <span className="truncate text-left flex-1">{triggerLabel}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 ml-1 shrink-0" />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
            }}
            data-testid={testId ? `${testId}-clear` : undefined}
            title="Rimuovi selezione"
            className="absolute right-7 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-rose-600 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
        <Command
          filter={(itemValue, search) => {
            const p = list.find((x) => x.codice === itemValue);
            if (!p) return 0;
            const hay = `${p.codice} ${p.nome || ""} ${p.regione || ""}`.toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca sigla, nome o regione…"
                        data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>Nessuna provincia trovata.</CommandEmpty>
            <CommandGroup>
              {list.map((p) => (
                <CommandItem
                  key={p.codice}
                  value={p.codice}
                  onSelect={(v) => {
                    const newV = (v || "").toUpperCase();
                    onChange(newV === norm ? null : newV);
                    setOpen(false);
                  }}
                  data-testid={testId ? `${testId}-opt-${p.codice}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4",
                    norm === p.codice ? "opacity-100" : "opacity-0")} />
                  <div className="flex items-baseline gap-2 flex-1 min-w-0">
                    <span className="font-mono text-sm">{p.codice}</span>
                    <span className="text-slate-700 text-xs truncate">{p.nome}</span>
                    {p.regione && (
                      <span className="text-slate-400 text-xs truncate">— {p.regione}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
